import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useAdminSession } from "../../app/providers";
import type { Schemas } from "../workspace/api.generated";
import { AdminUsersView } from "./components/AdminUsersView";
import { useAdminUserMutation, useAdminUsers } from "./queries";
import type { AdminUserMutation } from "./queries";
import { parseAdminRoleCode, parseAdminUserId, parseAdminUserPage, parseAdminUserStatus } from "./schema";
import type { AdminUserCriteria } from "./schema";

const PAGE_SIZE = 20;

function paramsFor(criteria: AdminUserCriteria, adminUserId?: number) {
  const params = new URLSearchParams();
  if (criteria.status) params.set("status", criteria.status);
  if (criteria.roleCode) params.set("roleCode", criteria.roleCode);
  if (criteria.page) params.set("page", String(criteria.page));
  if (adminUserId) params.set("adminUserId", String(adminUserId));
  return params;
}

type Notice = { readonly tone: "information" | "success"; readonly text: string };

export function AdminUsersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const session = useAdminSession();
  const search = searchParams.toString();
  const criteria = useMemo<AdminUserCriteria>(() => {
    const params = new URLSearchParams(search);
    return {
      status: parseAdminUserStatus(params.get("status")),
      roleCode: parseAdminRoleCode(params.get("roleCode")),
      page: parseAdminUserPage(params.get("page")),
      size: PAGE_SIZE,
    };
  }, [search]);
  const adminUserId = parseAdminUserId(searchParams.get("adminUserId"));
  const list = useAdminUsers(criteria);
  const selected = list.data?.items.find((row) => row.id === adminUserId);
  const mutation = useAdminUserMutation();
  const [notice, setNotice] = useState<Notice>();
  const authorities = session.authSession?.authorities ?? [];

  useEffect(() => {
    const canonical = paramsFor(criteria, adminUserId);
    if (canonical.toString() !== search) setSearchParams(canonical, { replace: true });
  }, [adminUserId, criteria, search, setSearchParams]);

  useEffect(() => {
    if (!list.data || list.isFetching || !adminUserId || selected) return;
    setSearchParams(paramsFor(criteria), { replace: true });
  }, [adminUserId, criteria, list.data, list.isFetching, selected, setSearchParams]);

  useEffect(() => {
    if (!list.data || list.data.items.length > 0 || list.data.totalCount === 0 || criteria.page === 0) return;
    const lastPage = Math.max(0, Math.ceil(list.data.totalCount / criteria.size) - 1);
    setSearchParams(paramsFor({ ...criteria, page: lastPage }), { replace: true });
  }, [criteria, list.data, setSearchParams]);

  const complete = async (input: AdminUserMutation, result: unknown) => {
    if (input.kind === "create") {
      const created = result as Schemas["AdminUserResponse"];
      setNotice({ tone: "success", text: `${created.name} 계정을 생성했습니다. 로그인 ID는 ${created.loginId}입니다. 목록에서 TOTP 등록 상태를 확인해 주세요.` });
      setSearchParams(paramsFor({ status: "", roleCode: "", page: 0, size: PAGE_SIZE }, created.id));
      return;
    }
    if (input.adminUserId === session.actor?.id && (input.kind === "roles" || input.kind === "totp")) return;
    const labels = { roles: "역할을 변경", disable: "계정을 비활성화", totp: "TOTP를 초기화" } as const;
    setNotice({ tone: "information", text: `관리자 계정의 ${labels[input.kind]}했습니다. 서버 목록을 다시 조회했습니다.` });
  };

  return <AdminUsersView
    criteria={criteria}
    result={list.data}
    listState={list.isPending ? "loading" : list.isError ? "error" : "ready"}
    selectedId={adminUserId}
    selected={selected}
    currentAdminId={session.actor?.id}
    canManage={Boolean(
      authorities.includes("ROLE_SUPER")
      && authorities.includes("ADMIN_MANAGE")
    )}
    notice={notice}
    onSearch={(next) => { setNotice(undefined); setSearchParams(paramsFor({ ...next, page: 0 })); }}
    onPageChange={(page) => setSearchParams(paramsFor({ ...criteria, page }))}
    onSelect={(id) => { setNotice(undefined); setSearchParams(paramsFor(criteria, id)); }}
    onClearSelection={() => setSearchParams(paramsFor(criteria))}
    onRetryList={async () => {
      const result = await list.refetch();
      if (result.error) throw result.error;
    }}
    onMutation={(input) => mutation.mutateAsync(input)}
    onMutationComplete={complete}
  />;
}
